'use client';

import React, { useCallback, useEffect, useState } from 'react';
import { Gauge } from 'lucide-react';
import { useGlobalModalStack } from '@wtc/ui/components/global-modal-stack';
import { getAuthToken } from '../../lib/api/authenticated-fetch';
import {
  CHAT_TOKEN_USAGE_MODAL_ID,
  type ChatUsageResponse,
} from '../ChatTokenUsageModal';

interface ChatTokenUsageTriggerProps {
  chatId: string;
  apiBaseUrl?: string;
}

function formatCompactTokens(n: number): string {
  if (n <= 0) return '0';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

/**
 * Compact footer control: shows total tokens for this chat and opens the
 * usage modal on click.
 *
 * Always hits `/api/meridian/usage` (not the entity-chat message base URL) so
 * both Hub chat and entity chat surfaces share one endpoint.
 */
export function ChatTokenUsageTrigger({
  chatId,
  apiBaseUrl = '/api/meridian',
}: ChatTokenUsageTriggerProps) {
  const { openModal } = useGlobalModalStack();
  const [totalTokens, setTotalTokens] = useState<number | null>(null);
  const usageApiBase = apiBaseUrl.includes('entity-chat')
    ? '/api/meridian'
    : apiBaseUrl.replace(/\/$/, '');

  const refresh = useCallback(async () => {
    if (!chatId) return;
    try {
      const token = await getAuthToken();
      const headers: HeadersInit = {};
      if (token) headers.Authorization = `Bearer ${token}`;
      const res = await fetch(
        `${usageApiBase}/usage?chatId=${encodeURIComponent(chatId)}`,
        { headers, credentials: 'include' }
      );
      if (!res.ok) return;
      const data = (await res.json()) as ChatUsageResponse;
      setTotalTokens(data.totalTokens);
    } catch {
      // Fail-open: keep prior label / show icon only
    }
  }, [chatId, usageApiBase]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <button
      type="button"
      onClick={() => {
        openModal(CHAT_TOKEN_USAGE_MODAL_ID);
        void refresh();
      }}
      className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
      title="View token usage and context window"
      aria-label="View token usage and context window"
    >
      <Gauge className="h-3.5 w-3.5 shrink-0" aria-hidden />
      <span className="tabular-nums">
        {totalTokens == null ? 'Tokens' : `${formatCompactTokens(totalTokens)} tokens`}
      </span>
    </button>
  );
}
